/*
 * ReminderForm — shared "create / edit a task" form used by:
 *   • The Reminders modal on student & teacher profiles
 *   • The Reminders dashboard (new task button)
 *
 * UX notes:
 *   - Date is shown by default. Time is opt-in via "+ Add time".
 *   - Priority, assignee, and labels all use SearchableSelect with icons.
 *   - Labels auto-create on Enter inside the search box.
 *
 * Stays presentational — caller controls submit/save side effects.
 */

import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment-timezone';
import {
  FaRegCalendarAlt,
  FaRegBell,
  FaUserCircle,
  FaLink,
  FaRegStickyNote,
  FaTag,
  FaBuilding,
  FaPaperclip,
  FaPlus,
  FaTimes,
  FaFlag
} from 'react-icons/fa';

import { PRIORITY_OPTIONS } from './constants';
import {
  REMINDER_OFFSETS,
  list_team_members,
  list_labels,
  create_label,
  list_departments,
  create_department,
  team_member_by_uuid,
  linked_user_by_uuid,
  search_linked_users,
  upload_attachment
} from './api';
import SearchableSelect from './SearchableSelect';
import PriorityIcon from './PriorityIcon';
import './reminders.css';

// Title is auto-derived from the description (first line, capped at 80 chars
// at a word boundary). This keeps the form simple and matches how most
// inbox-style task tools work (Linear, Things, Apple Reminders).
const deriveTitle = (description) => {
  if (!description) return 'Untitled reminder';
  const firstLine = description.split('\n').find((l) => l.trim().length > 0) || description;
  const trimmed = firstLine.trim();
  if (trimmed.length <= 80) return trimmed;
  const cut = trimmed.slice(0, 80);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > 40 ? cut.slice(0, lastSpace) : cut).trim()}…`;
};

const EMPTY = {
  description: '',
  due_date: moment().format('YYYY-MM-DD'),
  due_time: moment().add(1, 'hour').format('HH:mm'),
  has_time: false,
  priority: 'Medium',
  assignee_uuids: [],
  linked_user_uuid: '',
  label_uuids: [],
  // Single-select FK on the reminder row. Empty string means "not chosen yet";
  // it is required before save, and defaults to the creator's own department
  // (see the form's initial state) so it rarely stays empty.
  department_uuid: '',
  alert_one: 15,
  alert_two: '',
  // Each entry: { key, name, type, url? }. The `key` is the durable S3 key
  // returned by upload_attachment; `url` is a short-lived signed read URL
  // used to render inline previews in the form before the reminder is saved.
  attachments: []
};

function SectionLabel({ icon: Icon, children }) {
  return (
    <div className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
      {Icon ? <Icon className="text-gray-400" size={11} /> : null}
      {children}
    </div>
  );
}

const inputCls =
  'w-full rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-800 focus:border-mytPrimary focus:outline-none focus:ring-1 focus:ring-mytPrimary/40';

function ReminderForm({
  initial,
  lockedLinkedUser,
  defaultAssigneeUuid,
  selfUuid, // uuid of the logged-in team member — labelled "Self" in the assignee dropdown
  onSubmit,
  onCancel,
  submitLabel
}) {
  // "Save" works in both create and edit modes — keeps the CTA short and consistent.
  const effectiveSubmitLabel = submitLabel || 'Save';
  const [form, setForm] = useState(() => {
    // Decide the initial assignees: editing → carry forward; otherwise default
    // to "self" if we know who that is.
    const initialAssignees = Array.isArray(initial?.assignee_uuids)
      ? initial.assignee_uuids
      : defaultAssigneeUuid
        ? [defaultAssigneeUuid]
        : selfUuid
          ? [selfUuid]
          : [];
    return {
      ...EMPTY,
      ...(initial || {}),
      assignee_uuids: initialAssignees,
      linked_user_uuid: lockedLinkedUser?.uuid || (initial && initial.linked_user_uuid) || '',
      // Default to the creator's own department. A reminder saved without one
      // lands with department_uuid = NULL, and a NULL can't match the Depart
      // filter's SQL equality — that's how 39% of pending reminders became
      // unreachable from every filtered view. The cache may still be cold on
      // this first render; the effect below seeds it once the team list lands.
      department_uuid:
        initial?.department_uuid ||
        (selfUuid ? team_member_by_uuid(selfUuid)?.department_uuid : '') ||
        '',
      attachments: Array.isArray(initial?.attachments) ? initial.attachments : []
    };
  });
  // Seed `team` with the self entry synchronously so the "Self" label shows
  // immediately, before the (mocked-async) team list resolves.
  const [team, setTeam] = useState(() => {
    const self = selfUuid ? team_member_by_uuid(selfUuid) : null;
    return self ? [self] : [];
  });
  // linkedUsers is the option pool for the linked-user picker. It is seeded
  // with the currently selected user (so the trigger renders a label when
  // editing) and refilled by an async typeahead while the user types.
  const [linkedUsers, setLinkedUsers] = useState(() => {
    const preset = lockedLinkedUser || linked_user_by_uuid(initial?.linked_user_uuid);
    return preset ? [preset] : [];
  });
  const [linkedQuery, setLinkedQuery] = useState('');
  // Toggle on the dashboard's reminder form between student and teacher search.
  // The profile modal locks the linked user, so this control is unused there.
  const [linkedRole, setLinkedRole] = useState(() => {
    const preset =
      lockedLinkedUser?.role ||
      linked_user_by_uuid(initial?.linked_user_uuid)?.role ||
      initial?.linked_user_role ||
      'student';
    return preset === 'teacher' ? 'teacher' : 'student';
  });
  const [linkedSearching, setLinkedSearching] = useState(false);
  const [labels, setLabels] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  // Set only on a blocked submit, so the form doesn't shout at the user before
  // they've tried to save. Cleared as soon as a department is picked.
  const [departmentError, setDepartmentError] = useState('');

  useEffect(() => {
    list_team_members().then(setTeam);
    list_labels().then(setLabels);
    list_departments().then(setDepartments);
  }, []);

  // Seed the creator's department once the (async) team list resolves — the
  // cold-cache counterpart to the initial-state default above. Runs at most
  // once and only while the field is still empty, so it can never overwrite an
  // explicit choice, a value carried in from `initial`, or a deliberate clear
  // made after the list landed.
  const seededDepartmentRef = useRef(false);
  useEffect(() => {
    if (seededDepartmentRef.current) return;
    if (initial?.department_uuid) {
      seededDepartmentRef.current = true;
      return;
    }
    const mine = selfUuid ? team.find((m) => m.uuid === selfUuid)?.department_uuid : '';
    if (!mine) return;
    seededDepartmentRef.current = true;
    setForm((prev) => (prev.department_uuid ? prev : { ...prev, department_uuid: mine }));
  }, [team, selfUuid, initial]);

  // Debounced linked-user typeahead. Fires once the query is ≥3 chars,
  // querying only the currently-selected role (student or teacher). The
  // currently-selected user is kept in the option pool so the trigger
  // renders a label while editing. `linkedSearching` drives the inline
  // "Searching…" hint and suppresses the "No matches" empty state.
  useEffect(() => {
    const q = linkedQuery.trim();
    if (q.length < 3) {
      setLinkedSearching(false);
      return undefined;
    }
    setLinkedSearching(true);
    let cancelled = false;
    const timer = setTimeout(() => {
      search_linked_users(q, linkedRole)
        .then((rows) => {
          if (cancelled) return;
          const merged = [...rows];
          const selected = lockedLinkedUser || linked_user_by_uuid(form.linked_user_uuid) || null;
          if (selected && !merged.some((u) => u.uuid === selected.uuid)) {
            merged.unshift(selected);
          }
          setLinkedUsers(merged);
        })
        .finally(() => {
          if (!cancelled) setLinkedSearching(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [linkedQuery, linkedRole, lockedLinkedUser, form.linked_user_uuid]);

  const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const handleSubmit = (e) => {
    e?.preventDefault?.();
    if (!form.description.trim()) return;
    // Department is required in the UI. The server stays lenient (it only logs
    // a warning) because the ERC and sales-task quick-create buttons build
    // their own payload without this form and can legitimately fall back to
    // "", so a hard 400 would break them — see api_create.go. Enforcing here is
    // what actually stops new NULL-department reminders being created.
    if (!form.department_uuid) {
      setDepartmentError('Pick a department — reminders without one are hard to find later.');
      return;
    }
    setDepartmentError('');
    // Always send a full ISO timestamp — the form's `due_time` has a sane
    // default (now + 1 hour) even when has_time=false. has_time still
    // controls whether the UI surfaces the time, but the underlying
    // value is always meaningful so the tooltip / timezone math works.
    const due_at = moment(
      `${form.due_date} ${form.due_time || '09:00'}`,
      'YYYY-MM-DD HH:mm'
    ).toISOString();
    const alerts = [];
    if (form.alert_one !== '' && form.alert_one !== null && form.alert_one !== undefined) {
      alerts.push({ offset_minutes: Number(form.alert_one) });
    }
    if (form.alert_two !== '' && form.alert_two !== null && form.alert_two !== undefined) {
      alerts.push({ offset_minutes: Number(form.alert_two) });
    }
    const linkedUuid = lockedLinkedUser?.uuid || form.linked_user_uuid || null;
    // Resolve the linked user's role: locked-user prop wins; otherwise look up
    // the cached search result; fall back to the picker's role toggle.
    const linkedRoleResolved =
      lockedLinkedUser?.role ||
      (linkedUuid ? linked_user_by_uuid(linkedUuid)?.role : null) ||
      (linkedUuid ? linkedRole : null);
    // Strip the transient `url` field; only the durable key/name/type get
    // persisted, the server mints a fresh signed URL on every read.
    const attachmentsForSubmit = (form.attachments || []).map((a) => ({
      key: a.key,
      name: a.name || '',
      type: a.type || ''
    }));
    const payload = {
      // Carry forward the uuid when editing so the caller knows it's an update
      // rather than a create. When creating, `uuid` will be undefined.
      uuid: initial?.uuid,
      title: deriveTitle(form.description),
      description: form.description.trim(),
      due_at,
      has_time: form.has_time,
      priority: form.priority,
      assignee_uuids: form.assignee_uuids,
      linked_user_uuid: linkedUuid,
      linked_user_role: linkedRoleResolved,
      label_uuids: form.label_uuids,
      department_uuid: form.department_uuid || '',
      alerts,
      attachments: attachmentsForSubmit
    };
    setSubmitting(true);
    Promise.resolve(onSubmit(payload)).finally(() => setSubmitting(false));
  };

  /* ---------- Dropdown options ---------- */

  const priorityOptions = [
    ...PRIORITY_OPTIONS.map((p) => ({ value: p, label: p })),
    { value: 'None', label: 'None' }
  ];

  // The current user is labelled "Self" rather than their name/email, so the
  // common case ("assigned to me") reads fluently in the trigger. Other team
  // members keep their normal name + email display.
  const assigneeOptions = team.map((m) => ({
    value: m.uuid,
    label: selfUuid && m.uuid === selfUuid ? 'Self' : m.name,
    sublabel: m.email
  }));

  const linkedUserOptions = linkedUsers.map((u) => ({
    value: u.uuid,
    label: u.name,
    sublabel: u.email,
    avatar: u.avatar,
    role: u.role,
    email: u.email
  }));

  // Avatar element used by both the trigger (small) and the dropdown rows (larger).
  const renderAvatar = (opt, size) => {
    const px = `${size}px`;
    const cls = `shrink-0 rounded-full object-cover`;
    if (opt?.avatar) {
      return (
        <img
          src={opt.avatar}
          alt=""
          className={cls}
          style={{ width: px, height: px, aspectRatio: '1 / 1' }}
          onError={(e) => {
            e.currentTarget.style.display = 'none';
          }}
        />
      );
    }
    const initial = (opt?.label || '?').trim().charAt(0).toUpperCase();
    return (
      <span
        className="flex shrink-0 items-center justify-center rounded-full bg-gray-200 font-medium text-gray-600"
        style={{ width: px, height: px, aspectRatio: '1 / 1', fontSize: Math.round(size * 0.45) }}
      >
        {initial}
      </span>
    );
  };

  // Compact avatar for the closed trigger.
  const renderLinkedUserIcon = (opt) => renderAvatar(opt, 20);

  // Two-line row in the dropdown: avatar on the left, name + email stacked
  // on the right. Padding inside this node bumps the row height beyond the
  // SearchableSelect default.
  const renderLinkedUserRow = (opt, selected) => (
    <div className="flex w-full items-center gap-3 py-1">
      {renderAvatar(opt, 32)}
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-gray-800">{opt.label}</div>
        {opt.email ? <div className="truncate text-xs text-gray-500">{opt.email}</div> : null}
      </div>
      {selected ? <span className="text-xs text-green-600">✓</span> : null}
    </div>
  );

  const labelOptions = labels.map((l) => ({
    value: l.uuid,
    label: l.name,
    color: l.color
  }));

  const handleCreateLabel = (name) =>
    create_label(name).then((created) => {
      if (!created) return null;
      setLabels((prev) => (prev.some((l) => l.uuid === created.uuid) ? prev : [...prev, created]));
      return { value: created.uuid, label: created.name, color: created.color };
    });

  const departmentOptions = departments.map((d) => ({
    value: d.uuid,
    label: d.name,
    color: d.color
  }));

  const handleCreateDepartment = (name) =>
    create_department(name).then((created) => {
      if (!created) return null;
      setDepartments((prev) =>
        prev.some((d) => d.uuid === created.uuid) ? prev : [...prev, created]
      );
      return { value: created.uuid, label: created.name, color: created.color };
    });

  /* ---------- Attachments ---------- */

  // Hidden <input type="file"> driven by a visible paperclip button. We
  // upload immediately on selection so the operator sees the file in the
  // form before submitting — same UX as Slack / Linear / Gmail.
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [attachmentError, setAttachmentError] = useState('');

  const handleFilesPicked = (e) => {
    const files = Array.from(e.target.files || []);
    // Reset the input value so picking the same file twice still re-fires.
    if (e.target) e.target.value = '';
    if (files.length === 0) return;
    setAttachmentError('');
    setUploading(true);
    Promise.all(
      files.map((file) =>
        upload_attachment(file).catch((err) => {
          // eslint-disable-next-line no-console
          console.error('reminders.upload_attachment failed', err);
          return null;
        })
      )
    ).then((rows) => {
      const ok = rows.filter(Boolean);
      const failed = rows.length - ok.length;
      if (ok.length > 0) {
        update({ attachments: [...(form.attachments || []), ...ok] });
      }
      if (failed > 0) {
        setAttachmentError(`${failed} file${failed > 1 ? 's' : ''} failed to upload.`);
      }
      setUploading(false);
    });
  };

  const removeAttachment = (key) =>
    update({ attachments: (form.attachments || []).filter((a) => a.key !== key) });

  return (
    <form
      onSubmit={handleSubmit}
      className="reminders-scope flex flex-col items-stretch gap-3.5 text-left"
    >
      {/* Description — first line becomes the task title */}
      <div className="w-full">
        <SectionLabel icon={FaRegStickyNote}>What needs doing?</SectionLabel>
        <textarea
          autoFocus
          rows={5}
          style={{
            border: '1.5px solid var(--border-default)',
            borderRadius: 6,
            minHeight: 120,
            width: '100%',
            padding: '10px 12px',
            fontSize: 14,
            color: 'var(--text-primary)',
            background: 'var(--surface-card)',
            outline: 'none',
            resize: 'vertical'
          }}
          placeholder="e.g. Call Priya to confirm Saturday class — she works late so call after 6pm IST"
          value={form.description}
          onChange={(e) => update({ description: e.target.value })}
        />
        <div className="mt-1 text-[10px] text-gray-400">First line becomes the reminder title.</div>

        {/*
          Attachments. Each picked file uploads immediately so the operator
          can preview it in the form before submitting. The list below shows
          a small image/video thumb for media types and a name-only chip
          for everything else; clicking the X removes the attachment from
          the form's pending list (it's NOT deleted from S3 — orphaned
          uploads are bounded since we always rebuild the column from the
          form's submitted list, but we accept that minor cost).
        */}
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-2.5 py-1 text-[11px] font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
          >
            <FaPaperclip size={10} />
            {uploading ? 'Uploading…' : 'Attach file'}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={handleFilesPicked}
          />
          {attachmentError ? (
            <span className="text-[11px] text-red-600">{attachmentError}</span>
          ) : null}
        </div>
        {form.attachments && form.attachments.length > 0 ? (
          <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3">
            {form.attachments.map((a) => {
              const isImage = (a.type || '').startsWith('image/');
              const isVideo = (a.type || '').startsWith('video/');
              return (
                <div
                  key={a.key}
                  className="group relative rounded-md border border-gray-200 bg-white p-1.5"
                >
                  <button
                    type="button"
                    onClick={() => removeAttachment(a.key)}
                    aria-label={`Remove ${a.name || 'attachment'}`}
                    className="reminders-attachment-remove-btn absolute right-1 top-1 z-10 opacity-0 transition group-hover:opacity-100"
                  >
                    <FaTimes size={8} />
                  </button>
                  {isImage && a.url ? (
                    <img
                      src={a.url}
                      alt={a.name || 'attachment'}
                      className="h-20 w-full rounded object-cover"
                    />
                  ) : isVideo && a.url ? (
                    // muted + no controls keeps the form preview lightweight;
                    // the details modal renders a full <video controls> player.
                    <video src={a.url} className="h-20 w-full rounded object-cover" muted />
                  ) : (
                    <div className="flex h-20 w-full items-center justify-center rounded bg-gray-50 text-gray-400">
                      <FaPaperclip size={14} />
                    </div>
                  )}
                  <div className="mt-1 truncate text-[10px] text-gray-600" title={a.name || a.key}>
                    {a.name || a.key.split('/').pop()}
                  </div>
                </div>
              );
            })}
          </div>
        ) : null}
      </div>
      {/* Due date + (optional) time row */}
      <div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col">
            <SectionLabel icon={FaRegCalendarAlt}>Date</SectionLabel>
            <input
              type="date"
              className={`${inputCls} h-[34px] w-[170px] py-0`}
              value={form.due_date}
              onChange={(e) => update({ due_date: e.target.value })}
            />
          </div>
          <div className="flex flex-col">
            <SectionLabel>Time</SectionLabel>
            {form.has_time ? (
              <div className="flex items-center gap-1">
                <input
                  type="time"
                  className={`${inputCls} h-[34px] w-[130px] py-0`}
                  value={form.due_time}
                  onChange={(e) => update({ due_time: e.target.value })}
                />
                <button
                  type="button"
                  onClick={() => update({ has_time: false })}
                  className="reminders-icon-btn inline-flex h-7 w-7 items-center justify-center"
                  title="Remove time"
                  aria-label="Remove time"
                >
                  <FaTimes size={10} />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => update({ has_time: true })}
                className="reminders-dashed-btn inline-flex h-[34px] items-center gap-1 text-xs"
              >
                <FaPlus size={9} /> Add time
              </button>
            )}
          </div>
        </div>
      </div>
      {/* Priority */}
      <div>
        <SectionLabel icon={FaFlag}>Priority</SectionLabel>
        <SearchableSelect
          value={form.priority}
          options={priorityOptions}
          onChange={(v) => update({ priority: v })}
          placeholder="Set priority"
          renderIcon={(opt) => <PriorityIcon priority={opt.value} />}
          renderRow={(opt, selected) => (
            <>
              <PriorityIcon priority={opt.value} />
              <span className="flex-1 truncate">{opt.label}</span>
              {selected ? <span className="text-green-600 text-xs">✓</span> : null}
            </>
          )}
        />
      </div>
      {/* Assign to (multi) */}
      <div>
        <SectionLabel icon={FaUserCircle}>Assign to</SectionLabel>
        <SearchableSelect
          value={form.assignee_uuids}
          options={assigneeOptions}
          onChange={(v) => update({ assignee_uuids: v })}
          placeholder="Unassigned"
          multi
          clearable
          renderRow={(opt, selected) => (
            <>
              <FaUserCircle className="text-gray-400" size={13} />
              <span className="flex-1 truncate">{opt.label}</span>
              {opt.sublabel ? (
                <span className="text-[11px] text-gray-400">{opt.sublabel}</span>
              ) : null}
              {selected ? <span className="text-green-600 text-xs">✓</span> : null}
            </>
          )}
        />
      </div>
      {/*
        Department — single-select and REQUIRED (the list filter's department
        control is multi-select; this one deliberately is not). It defaults to
        the creator's own department. `clearable` is gone: dropping back to "no
        department" is what produced the NULL rows the Depart filter could
        never match. `onCreateOption` lets ops add a new department inline the
        same way Labels does.
      */}
      <div>
        <SectionLabel icon={FaBuilding}>Department *</SectionLabel>
        <SearchableSelect
          value={form.department_uuid}
          options={departmentOptions}
          onChange={(v) => {
            update({ department_uuid: v || '' });
            if (v) setDepartmentError('');
          }}
          placeholder="Select a department"
          onCreateOption={handleCreateDepartment}
          renderRow={(opt, selected) => (
            <>
              <FaBuilding className="text-gray-400" size={12} />
              <span className="flex-1 truncate">{opt.label}</span>
              {selected ? <span className="text-green-600 text-xs">✓</span> : null}
            </>
          )}
        />
        {departmentError ? (
          <div role="alert" className="mt-1 text-[11px] text-state-error-text">
            {departmentError}
          </div>
        ) : null}
      </div>
      {/* When opened from the dashboard (no locked user) we still need a
          way to pick the MYT user this task is about. */}
      {!lockedLinkedUser ? (
        <div>
          <div className="mb-1 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <SectionLabel icon={FaLink}>Linked user</SectionLabel>
              {linkedSearching ? (
                <span className="text-[10px] font-medium uppercase tracking-wider text-mytPrimary">
                  Searching…
                </span>
              ) : null}
            </div>
            <div
              className="inline-flex overflow-hidden rounded-md border border-gray-300 text-[10px] font-semibold uppercase tracking-wider"
              role="group"
              aria-label="Search role"
            >
              {['student', 'teacher'].map((r) => {
                const active = linkedRole === r;
                return (
                  <button
                    key={r}
                    type="button"
                    onClick={() => {
                      setLinkedRole(r);
                      // Keep only the currently-selected user when switching role
                      // so the dropdown isn't polluted with stale rows.
                      const selected =
                        lockedLinkedUser || linked_user_by_uuid(form.linked_user_uuid) || null;
                      setLinkedUsers(selected ? [selected] : []);
                    }}
                    // Inline styles beat the host's global `button { background:
                    // #cc5500 }` rule (which has !important via reminders.css
                    // helpers). The active state uses the MYT theme orange.
                    style={{
                      background: active ? 'var(--brand-primary)' : 'var(--surface-card)',
                      color: active ? 'var(--text-inverse)' : 'var(--text-secondary)',
                      border: 'none',
                      margin: 0,
                      padding: '2px 10px'
                    }}
                    className="capitalize"
                    aria-pressed={active}
                  >
                    {r}
                  </button>
                );
              })}
            </div>
          </div>
          <SearchableSelect
            value={form.linked_user_uuid}
            options={linkedUserOptions}
            onChange={(v) => update({ linked_user_uuid: v })}
            onQueryChange={setLinkedQuery}
            asyncOptions
            renderIcon={renderLinkedUserIcon}
            renderRow={renderLinkedUserRow}
            placeholder={`Type a name to search ${linkedRole}s`}
            emptyLabel={
              linkedQuery.trim().length < 3
                ? 'Type 3+ characters to search'
                : linkedSearching
                  ? 'Searching…'
                  : 'No matches'
            }
            clearable
          />
        </div>
      ) : null}
      {/* Labels (multi + auto-create) */}
      <div>
        <SectionLabel icon={FaTag}>Labels</SectionLabel>
        <SearchableSelect
          value={form.label_uuids}
          options={labelOptions}
          onChange={(v) => update({ label_uuids: v })}
          placeholder="Add labels"
          multi
          onCreateOption={handleCreateLabel}
          renderRow={(opt, selected) => (
            <>
              <span
                className="inline-block h-2.5 w-2.5 rounded-sm"
                style={{ background: opt.color || 'var(--border-default)' }}
              />
              <span className="flex-1 truncate">{opt.label}</span>
              {selected ? <span className="text-green-600 text-xs">✓</span> : null}
            </>
          )}
        />
      </div>
      {/* Alerts */}
      <div>
        <SectionLabel icon={FaRegBell}>Reminders (max 2)</SectionLabel>
        <div className="grid grid-cols-2 gap-3">
          <select
            className={inputCls}
            value={form.alert_one}
            onChange={(e) =>
              update({ alert_one: e.target.value === '' ? '' : Number(e.target.value) })
            }
          >
            <option value="">No reminder</option>
            {REMINDER_OFFSETS.map((r) => (
              <option key={`a1-${r.minutes}`} value={r.minutes}>
                {r.label}
              </option>
            ))}
          </select>
          <select
            className={inputCls}
            value={form.alert_two}
            onChange={(e) =>
              update({ alert_two: e.target.value === '' ? '' : Number(e.target.value) })
            }
          >
            <option value="">No second reminder</option>
            {REMINDER_OFFSETS.map((r) => (
              <option key={`a2-${r.minutes}`} value={r.minutes}>
                {r.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      {/* Submit row */}
      <div className="flex items-center justify-start gap-2 pt-1">
        <button
          type="submit"
          disabled={submitting || !form.description.trim()}
          className="reminders-primary-btn text-sm font-medium"
        >
          {submitting ? 'Saving…' : effectiveSubmitLabel}
        </button>
        {onCancel ? (
          <button type="button" onClick={onCancel} className="reminders-secondary-btn text-sm">
            Cancel
          </button>
        ) : null}
      </div>
    </form>
  );
}

export default ReminderForm;
